import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabase';

const C = {
  purple:"#691FB1", purpleLight:"#EDE7F6", purpleDark:"#736CCE",
  pink:"#FF357B", green:"#27AE60", greenLight:"#EAFAF1",
  amber:"#F39C12", amberLight:"#FEF9E7", blue:"#57B1DD",
  bg:"#F7F5FC", white:"#FFFFFF",
  text:"#1A1A2E", textMid:"#4A4A6A", textLight:"#8888AA", border:"#E4DEFA",
};

const COMM_PCT = { Novo:0.05, 'Retenção':0.02, Upsell:0.05, Cross:0.05 };
const TIERS = [
  { min:30000, bonus:300, pts:8 },
  { min:15000, bonus:200, pts:6 },
  { min:5000,  bonus:100, pts:4 },
  { min:500,   bonus:50,  pts:2 },
];
const PREMIOS = [ {pt:50,p:500},{pt:40,p:350},{pt:30,p:250},{pt:20,p:150},{pt:15,p:100},{pt:10,p:50} ];
export const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

export const eur = n => (n||0).toLocaleString("pt-PT",{minimumFractionDigits:0,maximumFractionDigits:0})+"€";
const eur2 = n => (n||0).toLocaleString("pt-PT",{minimumFractionDigits:2,maximumFractionDigits:2})+"€";

export function faturacaoProjetoMes(p, ym, sessoes, jaContados){
  const bm = p.billing_model;
  if(bm==='Mensal fixo'){
    if(!p.contract_start||!p.contract_end) return 0;
    const start=p.contract_start.slice(0,7), endD=new Date(p.contract_end);
    const last=new Date(endD); last.setDate(last.getDate()-1);
    const lastYM=last.getFullYear()+'-'+String(last.getMonth()+1).padStart(2,'0');
    return (ym>=start && ym<=lastYM) ? (p.value||0) : 0;
  }
  if(bm==='Único' || bm==='Por marco'){
    return (p.contract_start?.slice(0,7)===ym) ? (p.value||0) : 0;
  }
  if(bm==='Pré-pago'){
    const fatura = (p.billing_month && p.billing_month.length>=7) ? p.billing_month.slice(0,7) : p.contract_start?.slice(0,7);
    return (fatura===ym) ? (p.value||0) : 0;
  }
  if(bm==='Por sessão'){
    if(!p.cliente_id && !p.client) return 0;
    // BUG FIX: cliente com varios projetos 'Por sessao' contava as mesmas sessoes em duplicado
    const filtroAtv = (p.atividade_filtro||'').toLowerCase();
    const chaveCli = (p.cliente_id || (p.client||'').toLowerCase()) + '|' + filtroAtv;
    if(jaContados){ if(jaContados.has(chaveCli)) return 0; jaContados.add(chaveCli); }
    return (sessoes||[]).filter(s=> {
      if((s.date||'').slice(0,7)!==ym) return false;
      if(s.state!=='confirmed') return false; // BUG FIX: so sessoes confirmadas contam (antes somava canceladas e agendadas)
      if(filtroAtv && !(s.activity||'').toLowerCase().includes(filtroAtv)) return false; // projeto com atividade_filtro so conta essas sessoes
      // match por cliente_id quando ambos o tem (apanha localizacoes); senao cai no nome
      if(p.cliente_id && s.cliente_id) return s.cliente_id===p.cliente_id;
      return (s.client||'').toLowerCase()===(p.client||'').toLowerCase();
    }).reduce((a,s)=>a+(s.value_agreed||0),0);
  }
  return 0;
}

export default function CommissionsView({ perms, userName, userEmail, voltar }){
  const [projetos, setProjetos] = useState([]);
  const [sessoes, setSessoes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [comercialSel, setComercialSel] = useState('');
  const [mesAberto, setMesAberto] = useState(null);
  const [fechos, setFechos] = useState([]);   // comissões fechadas (trancadas) deste ano — comissoes_fecho
  const [aFechar, setAFechar] = useState(null);

  const podeVerTodos = perms?.canViewFinancial && (perms?.canEditFinancial || perms?.canManageUsers);

  useEffect(()=>{ load(); },[ano]);
  const load = async () => {
    setLoading(true);
    const ini = ano+'-01-01', fim = ano+'-12-31';
    const [{ data:projs }, { data:sess }, { data:us }] = await Promise.all([
      supabase.from('projects').select('*').eq('active',true).in('status',['Ativo','Concluído']).not('commercial_email','is',null),
      (async()=>{ let todas=[]; let de=0; while(true){ const { data } = await supabase.from('sessions').select('client,cliente_id,value_agreed,date,state,activity').gte('date',ini).lte('date',fim).order('date').range(de,de+999); todas=todas.concat(data||[]); if(!data||data.length<1000) break; de+=1000; } return { data: todas }; })(),
      supabase.from('user_roles').select('email,full_name').eq('active',true),
    ]);
    setProjetos(projs||[]); setSessoes(sess||[]); setUsers(us||[]);
    await carregarFechos();
    if(!comercialSel){
      if(podeVerTodos) setComercialSel('__todos__');
      else setComercialSel(userEmail||'');
    }
    setLoading(false);
  };

  const carregarFechos = async () => {
    try {
      const { data } = await supabase.from('comissoes_fecho').select('*').like('ano_mes', ano+'-%');
      setFechos(data||[]);
    } catch(e){ setFechos([]); }   // tabela ainda não criada
  };
  const podeFechar = !!(perms?.canEditFinancial || perms?.canManageUsers);

  const comerciais = useMemo(()=>{
    const set = {};
    projetos.forEach(p=>{ if(p.commercial_email) set[p.commercial_email]=p.commercial_name||p.commercial_email; });
    return Object.entries(set).map(([email,nome])=>({email,nome}));
  }, [projetos]);

  const projsFiltrados = useMemo(()=>{
    let arr = projetos;
    if(!podeVerTodos){ arr = arr.filter(p=>p.commercial_email===userEmail); }
    else if(comercialSel && comercialSel!=='__todos__'){ arr = arr.filter(p=>p.commercial_email===comercialSel); }
    return arr;
  }, [projetos, podeVerTodos, comercialSel, userEmail]);

  // Cálculo ao vivo, a partir dos projetos e do calendário (é o que vale para os meses ainda abertos).
  const calcular = (projsBase) => {
    // uma comissão fixa paga-se uma vez por projeto, não todos os meses
    const fixasPagas = new Set();
    return MESES.map((nome,i)=>{
      const ym = ano+'-'+String(i+1).padStart(2,'0');
      let fat=0, com=0; const det=[]; const jaContados = new Set();
      projsFiltrados.forEach(p=>{
        if(p.sem_comissao) return; // projeto marcado sem comissao: nao conta nada
        const f = faturacaoProjetoMes(p, ym, sessoes, jaContados);
        if(f>0){
          // COMISSÃO FIXA: quando definida, substitui o cálculo por percentagem.
          // Paga-se uma vez, no primeiro mês em que o projeto fatura.
          const fixa = Number(p.comissao_fixa)||0;
          if(fixa>0){
            if(!fixasPagas.has(p.id)){
              fixasPagas.add(p.id);
              fat += f; com += fixa;
              det.push({ cliente:p.client||p.name||'(sem cliente)', tipo:'fixa', pct:null,
                         fat:f, com:fixa, nota:p.comissao_fixa_nota||'' });
            } else {
              fat += f;   // a faturação conta, a comissão já foi paga
              det.push({ cliente:p.client||p.name||'(sem cliente)', tipo:'fixa (já paga)', pct:null,
                         fat:f, com:0, nota:p.comissao_fixa_nota||'' });
            }
            return;
          }
          // Decomposicao de comissao: se ha campos comissao_* preenchidos, usa taxa mista
          const decomp = (Number(p.comissao_retencao)||0)+(Number(p.comissao_upsell)||0)+(Number(p.comissao_novo)||0)+(Number(p.comissao_cross)||0);
          let pct, tipoLabel;
          if(decomp>0){
            const base = decomp; // soma das partes (deve ~= value)
            const taxa = ((Number(p.comissao_retencao)||0)*COMM_PCT['Retenção'] + (Number(p.comissao_upsell)||0)*COMM_PCT['Upsell'] + (Number(p.comissao_novo)||0)*COMM_PCT['Novo'] + (Number(p.comissao_cross)||0)*COMM_PCT['Cross']) / base;
            pct = taxa;
            const partes=[]; if(Number(p.comissao_retencao)>0)partes.push('Ret'); if(Number(p.comissao_upsell)>0)partes.push('Ups'); if(Number(p.comissao_novo)>0)partes.push('Novo'); if(Number(p.comissao_cross)>0)partes.push('Cross');
            tipoLabel = partes.join('+');
          } else {
            pct = COMM_PCT[p.tipo_negocio] ?? 0.05;
            tipoLabel = p.tipo_negocio||'Novo';
          }
          fat += f; com += f*pct;
          det.push({ cliente:p.client||p.name||'(sem cliente)', tipo:tipoLabel, pct, fat:f, com:f*pct });
        }
      });
      let bonus=0, pts=0;
      for(const t of TIERS){ if(fat>=t.min){ bonus=t.bonus; pts=t.pts; break; } }
      return { mes:nome, ym, fat, com, bonus, pts, det };
    });
  };

  // Meses FECHADOS: valem os valores gravados no fecho (não mudam mesmo que os projetos mudem depois).
  const emailsScope = useMemo(()=>{
    if(!podeVerTodos) return [userEmail];
    if(comercialSel && comercialSel!=='__todos__') return [comercialSel];
    return null;   // todos
  }, [podeVerTodos, comercialSel, userEmail]);
  const linhas = useMemo(()=>{
    const vivas = calcular(projsFiltrados);
    return vivas.map(l=>{
      const doMes = fechos.filter(f=>f.ano_mes===l.ym);
      if(!doMes.length) return l;
      const marca = doMes.find(f=>f.comercial_email==='__mes__') || doMes[0];
      const meus = doMes.filter(f=>f.comercial_email!=='__mes__' && (!emailsScope || emailsScope.includes(f.comercial_email)));
      const soma = k => meus.reduce((a,f)=>a+(Number(f[k])||0),0);
      return { ...l, fat:soma('faturado'), com:soma('comissao'), bonus:soma('bonus'), pts:soma('pts'),
               det: meus.flatMap(f=>Array.isArray(f.detalhe)?f.detalhe:[]),
               fechado:true, fechadoPor:marca.fechado_por, fechadoEm:marca.fechado_em };
    });
    // eslint-disable-next-line
  }, [projsFiltrados, sessoes, ano, fechos, emailsScope]);

  // Fechar o mês: grava, comercial a comercial, o que o HUB calcula agora para esse mês.
  const fecharMes = async (ym) => {
    const i = Number(ym.slice(5,7)) - 1;
    if(!window.confirm('Fechar as comissões de '+MESES[i]+' '+ano+'?\n\nOs valores deste mês ficam gravados para todos os comerciais e deixam de mudar, mesmo que alguém altere os projetos depois.')) return;
    setAFechar(ym);
    const quem = userName || userEmail || null, quando = new Date().toISOString();
    const rows = [{ ano_mes:ym, comercial_email:'__mes__', comercial_nome:null, faturado:0, comissao:0, bonus:0, pts:0, detalhe:[], fechado_por:quem, fechado_em:quando }];
    comerciais.forEach(c=>{
      const l = calcular(projetos.filter(p=>p.commercial_email===c.email))[i];
      if(!l || (l.fat<=0 && l.com<=0)) return;
      rows.push({ ano_mes:ym, comercial_email:c.email, comercial_nome:c.nome, faturado:Math.round(l.fat*100)/100, comissao:Math.round(l.com*100)/100,
                  bonus:l.bonus, pts:l.pts, detalhe:l.det, fechado_por:quem, fechado_em:quando });
    });
    const { error } = await supabase.from('comissoes_fecho').upsert(rows, { onConflict:'ano_mes,comercial_email' });
    if(error) alert('Não foi possível fechar o mês: '+error.message);
    await carregarFechos(); setAFechar(null);
  };
  const reabrirMes = async (ym) => {
    if(!window.confirm('Reabrir '+ym+'? Os valores gravados são apagados e o mês volta a ser calculado a partir dos projetos.')) return;
    const { error } = await supabase.from('comissoes_fecho').delete().eq('ano_mes', ym);
    if(error) alert('Não foi possível reabrir: '+error.message);
    await carregarFechos();
  };

  const tot = useMemo(()=>{
    const fat=linhas.reduce((a,l)=>a+l.fat,0);
    const com=linhas.reduce((a,l)=>a+l.com,0);
    const bonus=linhas.reduce((a,l)=>a+l.bonus,0);
    const pts=linhas.reduce((a,l)=>a+l.pts,0);
    let premio=0; for(const pr of PREMIOS){ if(pts>=pr.pt){ premio=pr.p; break; } }
    return { fat, com, bonus, pts, premio, totalReceber:com+bonus+premio };
  }, [linhas]);

  const card = (label,val,cor,icon)=>(
    <div style={{ background:C.white, borderRadius:12, border:"1px solid "+C.border, padding:'16px 18px', flex:1, minWidth:140 }}>
      <div style={{ fontSize:12, color:C.textLight, marginBottom:6, fontWeight:600 }}>{icon} {label}</div>
      <div style={{ fontSize:23, fontWeight:700, color:cor, fontFamily:"'Fraunces',serif" }}>{val}</div>
    </div>
  );

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif", padding:24 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Fraunces:wght@700&display=swap" rel="stylesheet"/>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18, gap:10, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          {voltar && <button onClick={voltar} style={{ background:C.bg, border:"1px solid "+C.border, borderRadius:8, color:C.textMid, padding:'7px 12px', fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:600 }}>← Orçamentos</button>}
          <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:24, color:C.text, margin:0 }}>Comissões · {ano}</h1>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button onClick={()=>setAno(a=>a-1)} style={{ background:C.white, border:"1px solid "+C.border, borderRadius:7, padding:'6px 11px', cursor:'pointer', fontSize:14, fontWeight:700, color:C.purple }}>‹</button>
          <span style={{ fontSize:14, fontWeight:700, color:C.text, minWidth:44, textAlign:'center' }}>{ano}</span>
          <button onClick={()=>setAno(a=>a+1)} style={{ background:C.white, border:"1px solid "+C.border, borderRadius:7, padding:'6px 11px', cursor:'pointer', fontSize:14, fontWeight:700, color:C.purple }}>›</button>
        </div>
      </div>

      {podeVerTodos && (
        <div style={{ marginBottom:16 }}>
          <select value={comercialSel} onChange={e=>setComercialSel(e.target.value)}
            style={{ padding:"8px 12px", border:"1px solid "+C.purple, borderRadius:8, fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none", minWidth:240 }}>
            <option value="__todos__">Todos os comerciais</option>
            {comerciais.map(c=><option key={c.email} value={c.email}>{c.nome}</option>)}
          </select>
        </div>
      )}
      {!podeVerTodos && (
        <div style={{ marginBottom:16, fontSize:13, color:C.textMid }}>As tuas comissões, {userName||userEmail}.</div>
      )}

      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        {card("Faturação anual", eur(tot.fat), C.purple, "💰")}
        {card("Comissão", eur2(tot.com), C.green, "💶")}

      </div>

      <div style={{ background:C.white, borderRadius:12, border:"1px solid "+C.border, overflow:'hidden' }}>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:C.bg }}>
                {["Mês","Faturação","Comissão",""].map((h,i)=>
                  <th key={h} style={{ padding:'10px 16px', fontSize:11, fontWeight:700, color:C.textLight, textAlign:i===0?'left':'right', textTransform:'uppercase', letterSpacing:0.5, borderBottom:"1px solid "+C.border, whiteSpace:'nowrap' }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={4} style={{ padding:40, textAlign:'center', color:C.textLight }}>A carregar...</td></tr>}
              {!loading && linhas.filter(l=>l.fat>0).length===0 && <tr><td colSpan={4} style={{ padding:40, textAlign:'center', color:C.textLight }}>Sem faturação com comissão neste ano.</td></tr>}
              {!loading && linhas.filter(l=>l.fat>0).map(l=>{
                const aberto = mesAberto===l.ym;
                const TN={Novo:['#EAFAF1','#27AE60'],'Retenção':['#E5F7FF','#1A6FA0'],Upsell:['#EDE7F6','#691FB1'],Cross:['#FFF0F5','#FF357B']};
                return (
                <React.Fragment key={l.ym}>
                <tr onClick={()=>setMesAberto(aberto?null:l.ym)} style={{ borderBottom:"1px solid "+C.border, cursor:'pointer', background:aberto?C.bg:'transparent' }}>
                  <td style={{ padding:'10px 16px', fontWeight:600, color:C.text }}>{aberto?'▾':'▸'} {l.mes}
                    {l.fechado && <span title={'Fechado por '+(l.fechadoPor||'?')+' em '+(l.fechadoEm||'').slice(0,10)} style={{ marginLeft:8, fontSize:10, fontWeight:700, color:C.purple, background:C.purpleLight, borderRadius:5, padding:'1px 7px' }}>🔒 fechado</span>}</td>
                  <td style={{ padding:'10px 16px', textAlign:'right', color:C.textMid }}>{eur2(l.fat)}</td>
                  <td style={{ padding:'10px 16px', textAlign:'right', fontWeight:700, color:C.green }}>{eur2(l.com)}</td>
                  <td style={{ padding:'10px 16px', textAlign:'right', whiteSpace:'nowrap' }} onClick={e=>e.stopPropagation()}>
                    {podeFechar && !l.fechado && l.ym < new Date().toISOString().slice(0,7) &&
                      <button onClick={()=>fecharMes(l.ym)} disabled={aFechar===l.ym} style={{ background:C.white, border:'1px solid '+C.purple, color:C.purple, borderRadius:7, padding:'4px 10px', fontSize:11.5, fontWeight:700, cursor:'pointer' }}>{aFechar===l.ym?'A fechar…':'Fechar mês'}</button>}
                    {podeFechar && l.fechado && perms?.canManageUsers &&
                      <button onClick={()=>reabrirMes(l.ym)} style={{ background:'none', border:'none', color:C.textLight, fontSize:11, cursor:'pointer', textDecoration:'underline' }}>reabrir</button>}
                  </td>

                </tr>
                {aberto && l.det.map((d,di)=>{ const c=TN[d.tipo]||(String(d.tipo).startsWith('fixa')?['#EDE7F6','#691FB1']:['#F7F5FC','#8888AA']); return (
                  <tr key={l.ym+'-'+di} style={{ borderBottom:"1px solid "+C.border, background:C.bg }}>
                    <td style={{ padding:'7px 16px 7px 34px', color:C.textMid, fontSize:12 }}>
                      {d.cliente} <span style={{ background:c[0], color:c[1], borderRadius:5, padding:'1px 7px', fontSize:10, fontWeight:700, marginLeft:6 }}>{d.tipo}</span>
                      {d.pct!=null
                        ? <span style={{ color:C.textLight, fontSize:10, marginLeft:6 }}>({(d.pct*100).toFixed(0)}%)</span>
                        : <span style={{ color:C.textLight, fontSize:10, marginLeft:6 }} title={d.nota||''}>(valor fixo)</span>}
                    </td>
                    <td style={{ padding:'7px 16px', textAlign:'right', color:C.textMid, fontSize:12 }}>{eur2(d.fat)}</td>
                    <td style={{ padding:'7px 16px', textAlign:'right', color:C.green, fontSize:12, fontWeight:600 }}>{eur2(d.com)}</td>
                    <td/>
                  </tr>
                ); })}
                </React.Fragment>
                );
              })}
            </tbody>
            {!loading && (
              <tfoot>
                <tr style={{ background:C.bg, borderTop:"2px solid "+C.border }}>
                  <td style={{ padding:'12px 16px', fontWeight:700, color:C.text }}>Total</td>
                  <td style={{ padding:'12px 16px', textAlign:'right', fontWeight:700, color:C.text }}>{eur2(tot.fat)}</td>
                  <td style={{ padding:'12px 16px', textAlign:'right', fontWeight:700, color:C.green }}>{eur2(tot.com)}</td>
                  <td/>

                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div style={{ marginTop:14, fontSize:11, color:C.textLight, lineHeight:1.6 }}>
        Comissão = faturação mensal real × % do tipo de negócio (Novo/Upsell/Cross 5%, Retenção 2%). Os valores acompanham a faturação real do Financeiro. Os meses com 🔒 estão fechados: mostram o que foi apurado e pago, e não mudam se os projetos forem alterados depois.
      </div>
    </div>
  );
}
